import { useState, useMemo, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { IconLayoutGrid, IconStar, IconStarFilled, IconSearch, IconArrowRight, IconCircleCheck, IconCoin, IconFlame, IconSparkles } from '@tabler/icons-react';
import { toBnDigits } from '../../../utils/bnFormat';
import { useTranslation } from '../../../i18n/hooks/useTranslation';
import { useToolFavoritesStore } from '../../../store/toolFavoritesStore';
import { useToolUsageStore } from '../../../store/toolUsageStore';
import { useAuthStore } from '../../../store/authStore';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import {
    ALL_TOOLS,
    isNewTool,
    CATS,
    CATEGORY_FILTERS,
    SITE_URL,
    SOON_SLUGS,
    PAID_SLUGS,
    isPaidTool,
    isInternalTool,
    isValidToolTab,
    toolIconSrc,
} from './toolCatalog';
import { squircleStyle, tileGradient } from './toolTile';
import { useHotToolSlugs } from './hotTools';

const PAID_COUNT = ALL_TOOLS.filter(isPaidTool).length;


export default function Tools() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { t, currentLanguage } = useTranslation();
    const isBn = currentLanguage === 'bn';

    const [query, setQuery] = useState('');
    // Pricing tab (top-right). Nearly every tool runs in the browser and is free;
    // the few that call the AI on the server cost credits and live in 'paid'.
    const [pricingTab, setPricingTab] = useState('free');
    const tabFromUrl = searchParams.get('tab');
    const [activeFilter, setActiveFilter] = useState(
        () => (tabFromUrl && isValidToolTab(tabFromUrl) ? tabFromUrl : 'all'),
    );

    useEffect(() => {
        const tab = searchParams.get('tab');
        setActiveFilter(tab && isValidToolTab(tab) ? tab : 'all');
    }, [searchParams]);

    const setFilter = (key) => {
        setActiveFilter(key);
        setSearchParams({ tab: key }, { replace: true });
    };

    // Server-backed favorites (keyed by tool slug), cached in localStorage.
    const favorites = useToolFavoritesStore((s) => s.favorites);
    const loadFavorites = useToolFavoritesStore((s) => s.load);
    const toggleFav = useToolFavoritesStore((s) => s.toggle);

    useEffect(() => { loadFavorites(); }, [loadFavorites]);

    // The user's own run counts (the ones each tool page shows as "N বার ব্যবহার হয়েছে"): the grid lists
    // the most-used tools first. Ties and never-used tools keep the catalogue order, so a new user sees
    // the default layout.
    const token = useAuthStore((s) => s.token);
    const usageCounts = useToolUsageStore((s) => s.counts);
    const loadUsage = useToolUsageStore((s) => s.load);
    useEffect(() => { if (token) loadUsage(); }, [token, loadUsage]);

    // The hot tools: top 3 per category across all users (shared with the dashboard).
    const hotSet = useHotToolSlugs();
    // soon / paid win over hot, hot wins over new (new = added in the last NEW_WINDOW_DAYS days)
    const badgeFor = useMemo(
        () => (tool) => (SOON_SLUGS.has(tool.slug) ? 'soon' : PAID_SLUGS.has(tool.slug) ? 'paid' : hotSet.has(tool.slug) ? 'hot' : isNewTool(tool) ? 'new' : null),
        [hotSet],
    );

    // Category counts for the filter-chip badges.
    // The two pricing tabs each have their own catalogue, so the category chips,
    // their counts and the search all work inside whichever tab is showing.
    const tabTools = useMemo(
        () => ALL_TOOLS.filter((tool) => isPaidTool(tool) === (pricingTab === 'paid')),
        [pricingTab],
    );

    const counts = useMemo(() => {
        const c = { all: tabTools.length, fav: 0, new: 0, hot: 0 };
        for (const tool of tabTools) {
            c[tool.categoryId] = (c[tool.categoryId] || 0) + 1;
            if (favorites.has(tool.slug)) c.fav += 1;
            const badge = badgeFor(tool);
            if (badge === 'new') c.new += 1;
            else if (badge === 'hot') c.hot += 1;
        }
        return c;
    }, [favorites, tabTools, badgeFor]);

    const filtered = useMemo(() => {
        let list = tabTools.filter((tool) => {
            if (activeFilter === 'fav') return favorites.has(tool.slug);
            if (activeFilter === 'new' || activeFilter === 'hot') return badgeFor(tool) === activeFilter; // exactly the tools wearing that badge
            if (activeFilter !== 'all') return tool.categoryId === activeFilter;
            return true;
        });
        const q = query.trim().toLowerCase();
        if (q) list = list.filter((tool) =>
            [tool.name, tool.nameBn, tool.categoryTitle, tool.categoryTitleBn]
                .some((s) => (s || '').toLowerCase().includes(q)));
        const used = token ? usageCounts : {};
        return [...list].sort((a, b) => (used[b.slug] || 0) - (used[a.slug] || 0));
    }, [tabTools, activeFilter, query, favorites, usageCounts, token, badgeFor]);

    return (
        <div className="w-full font-hind py-2 text-zinc-900 sm:py-4 dark:text-zinc-100">
            {/* ── Header ── */}
            <div className="mb-[22px] flex flex-wrap items-end justify-between gap-[18px]">
                <div className="flex items-center gap-[11px]">
                    <div className="flex h-[42px] w-[42px] flex-none items-center justify-center rounded-xl bg-zinc-900 text-white dark:bg-zinc-100 dark:text-zinc-900">
                        <IconLayoutGrid size={22} stroke={1.6} />
                    </div>
                    <div>
                        <div className="flex flex-wrap items-center gap-2.5">
                            <span className="text-[25px] font-bold tracking-[-0.01em] text-zinc-900 dark:text-zinc-100">
                                {t('toolsPage.title')}
                            </span>
                            {/* Beside the title: says what kind of tools the open tab holds */}
                            {pricingTab === 'paid' ? (
                                <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2.5 py-0.5 text-[11.5px] font-semibold text-amber-700 dark:bg-amber-900/20 dark:text-amber-400">
                                    <IconCoin size={13} stroke={2} />
                                    {t('toolsPage.allPaid')}
                                </span>
                            ) : (
                                <span className="animate-pulse inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-0.5 text-[11.5px] font-semibold text-emerald-600 dark:bg-emerald-900/20 dark:text-emerald-400">
                                    <IconCircleCheck size={13} stroke={2} />
                                    {t('toolsPage.allFree')}
                                </span>
                            )}
                        </div>
                        <div className="mt-0.5 text-[13.5px] text-zinc-500 dark:text-zinc-400">
                            {t('toolsPage.subtitle', { count: toBnDigits(ALL_TOOLS.length - PAID_COUNT) })}
                        </div>
                    </div>
                </div>
                {/* Free / Paid pricing tabs */}
                <div className="flex items-center gap-1 rounded-[10px] border border-zinc-200 bg-white p-1 dark:border-zinc-700 dark:bg-zinc-900">
                    {[{ key: 'free', label: t('toolsPage.freeTools'), count: ALL_TOOLS.length - PAID_COUNT },
                      { key: 'paid', label: t('toolsPage.paidTools'), count: PAID_COUNT }]
                        .map((p) => {
                            const active = pricingTab === p.key;
                            return (
                                <button
                                    key={p.key}
                                    onClick={() => setPricingTab(p.key)}
                                    className={`flex items-center gap-1.5 rounded-[7px] px-[13px] py-1.5 text-[12.5px] font-medium transition-colors ${
                                        active
                                            ? 'bg-zinc-900 text-white dark:bg-zinc-100 dark:text-zinc-900'
                                            : 'text-zinc-500 hover:bg-zinc-100 dark:text-zinc-400 dark:hover:bg-zinc-800'
                                    }`}
                                >
                                    {p.label}
                                    <span className={`rounded-[20px] px-[6px] py-px text-[10.5px] font-bold ${
                                        active
                                            ? 'bg-white/[0.18] text-white dark:bg-zinc-900/20 dark:text-zinc-900'
                                            : 'bg-zinc-100 text-zinc-500 dark:bg-zinc-800 dark:text-zinc-400'
                                    }`}>
                                        {toBnDigits(p.count)}
                                    </span>
                                </button>
                            );
                        })}
                </div>
            </div>

            {/* ── Search ── */}
            <div className="relative mb-4">
                <IconSearch size={19} stroke={1.8} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400 dark:text-zinc-500" />
                <input
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder={t('toolsPage.searchPlaceholder')}
                    className="w-full rounded-[13px] border border-zinc-200 bg-white py-[14px] pl-[46px] pr-4 text-[14.5px] text-zinc-900 outline-none placeholder:text-zinc-400 focus:border-primary focus:ring-2 focus:ring-primary/30 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100"
                />
            </div>

            {/* ── Filters ── */}
            <div className="mb-5 flex flex-nowrap overflow-x-auto items-center gap-2 pb-1 sm:flex-wrap sm:gap-[9px] [scrollbar-width:none] [-ms-overflow-style:none]">
                {[{ key: 'all', label: t('toolsPage.filters.all'), Icon: IconLayoutGrid },
                  { key: 'fav', label: t('toolsPage.filters.fav'), Icon: IconStar },
                  { key: 'new', label: t('toolsPage.filters.new'), Icon: IconSparkles },
                  { key: 'hot', label: t('toolsPage.filters.hot'), Icon: IconFlame },
                  ...CATEGORY_FILTERS.map((f) => ({ key: f.key, label: isBn ? f.titleBn : f.title, Icon: f.Icon }))]
                    .map((f) => {
                        const active = activeFilter === f.key;
                        return (
                            <button
                                key={f.key}
                                onClick={() => setFilter(f.key)}
                                className={`flex items-center gap-2 whitespace-nowrap rounded-[11px] border px-[15px] py-[9px] text-[13.5px] transition-colors ${
                                    active
                                        ? 'border-zinc-900 bg-zinc-900 font-semibold text-white dark:border-zinc-100 dark:bg-zinc-100 dark:text-zinc-900'
                                        : 'border-zinc-200 bg-white font-medium text-zinc-600 hover:border-zinc-300 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-300'
                                }`}
                            >
                                <f.Icon size={15} stroke={1.7} />
                                {f.label}
                                <span className={`rounded-[20px] px-[7px] py-px text-[11px] font-bold ${
                                    active
                                        ? 'bg-primary text-white'
                                        : 'bg-primary/10 text-primary dark:bg-primary/20'
                                }`}>
                                    {toBnDigits(counts[f.key] || 0)}
                                </span>
                            </button>
                        );
                    })}
            </div>

            {/* ── Tool grid ── */}
            {filtered.length === 0 ? (
                <div className="flex flex-col items-center justify-center rounded-[14px] border border-dashed border-zinc-200 py-20 text-center dark:border-zinc-700">
                    <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-zinc-100 text-zinc-400 dark:bg-zinc-800">
                        <IconLayoutGrid size={26} stroke={1.6} />
                    </div>
                    <p className="mt-3 text-sm text-zinc-500 dark:text-zinc-400">{pricingTab === 'paid' ? t('toolsPage.zeroToolsFound') : t('toolsPage.empty')}</p>
                </div>
            ) : (
                <div className="grid grid-cols-4 gap-x-2 gap-y-3 sm:grid-cols-[repeat(auto-fill,minmax(180px,1fr))] sm:gap-x-3.5 sm:gap-y-6">
                    {filtered.map((tool) => {
                        const isFav = favorites.has(tool.slug);
                        const badge = badgeFor(tool);
                        const title = isBn ? tool.nameBn : tool.name;
                        const subtitle = isBn ? tool.name : (tool.categoryTitle || '');
                        const open = () => isInternalTool(tool)
                            ? navigate(tool.path)
                            : window.open(SITE_URL + tool.path, '_blank', 'noopener,noreferrer');
                        return (
                            <div
                                key={tool.id}
                                onClick={open}
                                className="group flex cursor-pointer flex-col items-center rounded-xl px-0 py-1.5 text-center transition-colors hover:bg-white dark:hover:bg-zinc-800 sm:rounded-3xl sm:px-2 sm:py-5"
                            >
                                <div className="relative w-full sm:w-auto">
                                    {/* Gradient icon tile */}
                                    <div style={{ ...squircleStyle, backgroundImage: tileGradient(tool) }} className="flex aspect-square w-full items-center justify-center drop-shadow-lg transition-transform duration-150 group-hover:scale-[1.03] sm:w-[150px]">
                                        {tool.iconImg
                                            ? <img src={toolIconSrc(tool.iconImg)} alt={title} className="h-3/5 w-3/5 object-contain brightness-0 invert sm:h-[112px] sm:w-[112px]" loading="lazy" />
                                            : <tool.IconComp stroke={1.6} className="text-white w-3/5 h-3/5" />}
                                    </div>

                                    {/* Hot / New badge */}
                                    {badge && (
                                        <span className={`absolute -left-0.5 -top-0.5 rounded-full px-1.5 py-0 text-[8px] sm:-left-1 sm:-top-1 sm:px-2.5 sm:py-[3px] sm:text-[10px] font-bold text-white shadow-md ${
                                            badge === 'hot' ? 'bg-red-500' : badge === 'soon' ? 'bg-zinc-500' : badge === 'paid' ? 'bg-amber-700' : 'bg-emerald-600'
                                        }`}>
                                            {t(`toolsPage.badge.${badge}`)}
                                        </span>
                                    )}

                                    {/* Favorite toggle — hidden until the tile is hovered
                                        (desktop); already-favorited tools keep it visible.
                                        Mobile has no hover, so it stays visible there. */}
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <button
                                                type="button"
                                                onClick={(e) => { e.stopPropagation(); toggleFav(tool.slug); }}
                                                aria-label={isFav ? t('toolsPage.removeFavorite') : t('toolsPage.addFavorite')}
                                                className={`absolute -bottom-0.5 right-0 flex h-6 w-6 items-center justify-center rounded-full bg-white/20 backdrop-blur-md backdrop-sepia-[0.1] shadow-md transition-[opacity,transform] hover:scale-110 sm:-bottom-1 sm:right-1 sm:h-9 sm:w-9 ${
                                                    isFav ? 'opacity-100' : 'opacity-100 sm:opacity-0 sm:group-hover:opacity-100'
                                                }`}
                                            >
                                                {isFav
                                                    ? <IconStarFilled className="text-primary w-3.5 h-3.5 sm:w-[19px] sm:h-[19px]" />
                                                    : <IconStar stroke={1.9} className="text-white w-3.5 h-3.5 sm:w-[19px] sm:h-[19px]" />}
                                            </button>
                                        </TooltipTrigger>
                                        <TooltipContent>
                                            {isFav ? t('toolsPage.removeFavorite') : t('toolsPage.addFavorite')}
                                        </TooltipContent>
                                    </Tooltip>
                                </div>

                                <div className="mt-1 w-full px-0.5 text-[9px] leading-tight font-semibold text-zinc-800 dark:text-zinc-100 sm:mt-4 sm:max-w-[170px] sm:text-[15.5px]">
                                    {title}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
